/**
 * PeptidesForMedSpas — Evidence-Tier Registry
 * Maps every catalog product (by URL slug) to a regulatory tier per our
 * /editorial-standards methodology:
 *
 *   Tier 1 — FDA-Approved Pharmaceutical (regulated drug product)
 *   Tier 2 — Compounded / Off-Label Use of Approved Product (503A/503B + Rx)
 *   Tier 3 — Research-Only Compound (laboratory use only; not FDA-approved)
 *
 * Everything in our actual catalog is Tier 3. The Tier 1/Tier 2 entries listed
 * under WELL_KNOWN_TIERS are referenced elsewhere on the site (e.g., GLP-1 wall
 * on /weight-loss-peptides, Vyleesi callout on /peptides/pt-141) for editorial
 * compare-and-contrast — they are NOT products we sell.
 */

const TIER = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
};

// Product-slug → tier metadata.
// Slugs must match SLUG_MAP in pages/PeptideDetailPage.jsx + ResearchCatalog
// product slugs / buy links. All current products are Tier 3 (research-only).
export const PEPTIDE_TIERS = {
  "ghk-cu":              { tier: TIER.THREE, label: "Research", note: "GHK-Cu is studied in dermatology and skin-research literature; supplied for laboratory use only." },
  "nad":                 { tier: TIER.THREE, label: "Research", note: "Research material; clinical NAD+ precursor work is conducted with regulated products under clinician supervision." },
  "5-amino-1mq":         { tier: TIER.THREE, label: "Research", note: "Animal-model and cell-culture data; sold for laboratory research only." },
  "bpc-157":             { tier: TIER.THREE, label: "Research", note: "Preclinical animal data only; no FDA-approved formulation for human use." },
  "tb-500":              { tier: TIER.THREE, label: "Research", note: "Thymosin-Beta-4 fragment; research material. Anti-doping considerations apply for athletes." },
  "sermorelin":          { tier: TIER.THREE, label: "Research", note: "GHRH 1-29 research material. Any clinical GH-axis work uses regulated products under endocrinology oversight." },
  "mots-c":              { tier: TIER.THREE, label: "Research", note: "Mitochondrial-derived peptide studied in metabolism research models." },
  "pt-141":              { tier: TIER.THREE, label: "Research", note: "Research-only PT-141 is distinct from Vyleesi® (FDA-approved bremelanotide injection)." },
  "klotho":              { tier: TIER.THREE, label: "Research", note: "Anti-α-Klotho monoclonal antibody — a laboratory reagent (Western blot/IHC/ELISA), not a therapeutic." },
};

// Editorially-referenced Tier 1 / Tier 2 entries — NOT sold here.
// Used by callouts on pillar pages and product pages for compare/contrast.
export const WELL_KNOWN_TIERS = {
  semaglutide_branded:   { tier: TIER.ONE,   label: "FDA-Approved", note: "Approved semaglutide product class for an FDA-defined clinical indication. Prescription only; dispensed by licensed pharmacies." },
  tirzepatide_branded:   { tier: TIER.ONE,   label: "FDA-Approved", note: "Approved tirzepatide product class for an FDA-defined clinical indication. Prescription only." },
  bremelanotide_vyleesi: { tier: TIER.ONE,   label: "FDA-Approved", note: "Vyleesi® bremelanotide injection — FDA-approved for a specific indication in adult premenopausal women. Prescription only." },
  compounded_glp1:       { tier: TIER.TWO,   label: "Compounded",  note: "Compounded GLP-1 medications dispensed by licensed 503A/503B pharmacies under a clinician's order, where regulatorily permissible." },
};

// Visual style per tier — used by <TierBadge />.
export const TIER_STYLE = {
  [TIER.ONE]: {
    name: "Tier 1 — FDA-Approved",
    short: "Tier 1",
    bgClass: "bg-emerald-50",
    borderClass: "border-emerald-300",
    textClass: "text-emerald-700",
    dotClass: "bg-emerald-500",
    anchor: "tier-1",
  },
  [TIER.TWO]: {
    name: "Tier 2 — Compounded / Off-Label",
    short: "Tier 2",
    bgClass: "bg-amber-50",
    borderClass: "border-amber-300",
    textClass: "text-amber-700",
    dotClass: "bg-amber-500",
    anchor: "tier-2",
  },
  [TIER.THREE]: {
    name: "Tier 3 — Research-Only",
    short: "Tier 3",
    bgClass: "bg-rose-50",
    borderClass: "border-rose-300",
    textClass: "text-rose-700",
    dotClass: "bg-rose-500",
    anchor: "tier-3",
  },
};

export function getTierForSlug(slug) {
  return PEPTIDE_TIERS[slug] || { tier: TIER.THREE, label: "Research", note: "Default classification: research-only material." };
}

// Reverse: API peptide records expose `id` (string). The SLUG_MAP in
// PeptideDetailPage links slug -> dbId. Mirror that mapping here so product
// cards in ResearchCatalog / pillar grids can resolve a tier from a peptide
// fetched from /api/peptides without re-fetching the SLUG_MAP.
const DB_ID_TO_SLUG = {
  "1": "ghk-cu",
  "3": "nad",
  "4": "5-amino-1mq",
  "5": "bpc-157",
  "6": "tb-500",
  "8": "sermorelin",
  "9": "mots-c",
  "10": "pt-141",
  "11": "klotho",
};

export function getTierForPeptide(peptide) {
  if (!peptide) return getTierForSlug(null);
  const slug = DB_ID_TO_SLUG[String(peptide.id)];
  return getTierForSlug(slug);
}

export function getSlugForPeptide(peptide) {
  if (!peptide) return null;
  return DB_ID_TO_SLUG[String(peptide.id)] || null;
}

export { TIER };
